import Grid2 from '@mui/material/Unstable_Grid2';
import { useId } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Eyebrow from '@/components/Eyebrow/Eyebrow';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer2, {
  gridMargins,
} from '@/components/Layout/GridContainer2/GridContainer2';
import Link from '@/components/Link/Link';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import {
  type Contentful,
  ContentStyle,
  type FrontifyImageContent,
  type LinkContent,
} from '@/contentful';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { ViewportSize } from '@/styles/viewportSize';
import { getFrontifyBackgroundImageUrl } from '@/utils/frontify';

import useStyles, { WAYFINDING_CARD_WIDTH } from './Wayfinding.styles';

export interface LinkCardContent extends Contentful {
  fields: {
    image: FrontifyImageContent[];
    eyebrow?: string;
    link: LinkContent;
    body?: string;
  };
}

export interface WayfindingContent extends Contentful {
  fields: {
    style: ContentStyle;
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    linkCards: LinkCardContent[];
  };
}

export interface WayfindingProps {
  content: WayfindingContent;
}

// Calculate if buttons should be shown by determining if card content fits in the region within the grid and right margin
export function shouldShowNavigation(
  viewportWidth: number,
  viewportSize: ViewportSize,
  numberOfCards: number
): boolean {
  // Gap between slides matching spacing(3)
  const slideGap = viewportWidth < ViewportSize.lg ? 40 : 64;
  // On fixed grid, extra margin is 100vw - 1440
  const fixedMargin =
    viewportWidth < ViewportSize.lg ? 0 : (viewportWidth - ViewportSize.lg) / 2;
  // Slider width is 260px per card plus gap spacing
  const sliderWidth =
    WAYFINDING_CARD_WIDTH * numberOfCards + slideGap * (numberOfCards - 1);
  // Available space for swiper is viewport width minus 1 grid margin and half of the fixed extra margin
  const availableSliderWidth =
    viewportWidth - (gridMargins[viewportSize] + fixedMargin);
  // Hide button if content is within overflow
  return sliderWidth > availableSliderWidth;
}

export default function Wayfinding({
  content: {
    fields: { style, eyebrow, heading, subheading, linkCards },
  },
}: WayfindingProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const isOnDark = style === ContentStyle.OnDark;
  const { classes, cx } = useStyles({ isOnDark });
  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');

  const { viewportWidth, viewportSize } = useViewPortSize();

  const showNavigation = shouldShowNavigation(
    viewportWidth,
    viewportSize,
    linkCards.length
  );
  const showCardEyebrows = linkCards.every(
    (linkCard) => linkCard.fields.eyebrow
  );
  const showCardBodies = linkCards.every((linkCard) => linkCard.fields.body);

  return (
    <SpacingContainer className={cx(classes.container, style)}>
      <GridContainer2 fixed>
        <Grid2 xs={12} md={6}>
          <SectionHeader
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
            isOnDark={isOnDark}
            fixed
            headingSpacingValue={1}
          />
        </Grid2>
      </GridContainer2>
      <GridContainer2 fixed gridProps={{ className: classes.cardContainer }}>
        <Grid2 xs={12} className={classes.grid}>
          <Swiper
            navigation={{
              prevEl: `#${prevElId}`,
              nextEl: `#${nextElId}`,
              disabledClass: classes.navigationButtonDisabled,
            }}
            modules={[Navigation]}
            slidesPerView={'auto'}
          >
            {linkCards.map(
              (
                {
                  fields: {
                    image,
                    eyebrow,
                    link: {
                      fields: { text, url },
                    },
                    body,
                  },
                },
                index
              ) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                <SwiperSlide className={classes.swiperSlide} key={index}>
                  <Link className={classes.card} href={url}>
                    <div
                      className={cx(
                        classes.cardImageContainer,
                        showCardEyebrows && classes.cardImageContainerEyebrow
                      )}
                    >
                      <div
                        className={classes.cardImage}
                        style={{
                          backgroundImage: getFrontifyBackgroundImageUrl(
                            image[0],
                            ViewportSize.sm
                          ),
                        }}
                      />
                    </div>
                    {showCardEyebrows && (
                      <Eyebrow text={eyebrow as string} isOnDark={isOnDark} />
                    )}
                    <p
                      className={cx(
                        globalClasses.body2,
                        classes.cardLink,
                        showCardEyebrows && classes.cardLinkEyebrow,
                        style
                      )}
                    >
                      {text}
                    </p>
                    {showCardBodies && (
                      <p
                        className={cx(
                          globalClasses.body,
                          classes.cardBody,
                          style
                        )}
                      >
                        {body}
                      </p>
                    )}
                  </Link>
                </SwiperSlide>
              )
            )}
          </Swiper>
          <div
            className={cx(
              classes.navigation,
              !showNavigation && classes.hidden
            )}
          >
            <div id={prevElId} className={cx(classes.navigationButton, style)}>
              <Icon name={IconName.ARROW_LEFT} width={24} />
            </div>
            <div id={nextElId} className={cx(classes.navigationButton, style)}>
              <Icon name={IconName.ARROW_RIGHT} width={24} />
            </div>
          </div>
        </Grid2>
      </GridContainer2>
    </SpacingContainer>
  );
}
